import type { Effect } from "../content/effects";
import {
  ITEMS,
  itemById,
  levelOf,
  type Item,
  type ItemKind,
  type ItemLevel,
} from "../content/items";
import type { Ninja } from "../content/ninjas";

/*
  Items: what the child owns, what a purchase may do, and which items a
  ninja carries into battle. Owning is by level (buying gives level 1, an
  upgrade the next); a loadout is per ninja and is only ever a wish list,
  since `fitFor` decides what's actually carried. Pure.
*/

export interface Owned {
  level: number;
  /** ISO date first bought. */
  bought: string;
}
/** items[itemId]: the level held. Missing means not owned. */
export type OwnedItems = Record<string, Owned>;

export interface Loadout {
  active: (string | null)[];
  passive: (string | null)[];
}
/** loadout[ninjaId]: what the child fitted to each ninja's slots. */
export type Loadouts = Record<string, Loadout>;

export interface KitEntry {
  item: Item;
  level: number;
  /** The level's text, effect and price. */
  at: ItemLevel;
}
/** What a ninja actually carries: owned items in slots of their kind, no repeats. */
export interface Kit {
  active: KitEntry[];
  passive: KitEntry[];
}

export const EMPTY_KIT: Kit = { active: [], passive: [] };

// ─── Owning and buying ──────────────────────────────────────────────────────

export function levelHeld(items: OwnedItems, id: string): number {
  return items[id]?.level ?? 0;
}

/** Coins the child can spend. */
export function balanceOf(p: { coins: number; spent: number }): number {
  return Math.max(0, p.coins - p.spent);
}

/** The next level of an item the child could buy, or null once it's maxed. */
export function nextLevel(items: OwnedItems, item: Item): { level: number; at: ItemLevel } | null {
  const level = levelHeld(items, item.id) + 1;
  const at = levelOf(item, level);
  return at ? { level, at } : null;
}

/**
 * Whether a purchase can apply: the item exists, the level is exactly the
 * next one, and the balance covers the price. The fold uses this so two
 * devices buying the same level offline end with one level and one price
 * paid, and a purchase the coins can't cover folds as nothing. The server
 * checks the same, plus the price against its price list.
 */
export function purchaseAllowed(
  items: OwnedItems,
  balance: number,
  id: string,
  level: number,
  price: number,
): boolean {
  const item = itemById(id);
  if (!item || !levelOf(item, level)) return false;
  if (level !== levelHeld(items, id) + 1) return false;
  return price >= 0 && price <= balance;
}

/** Items with a next level the balance covers, for "you can afford something new". */
export function affordable(items: OwnedItems, balance: number): Item[] {
  return ITEMS.filter((it) => {
    const next = nextLevel(items, it);
    return !!next && next.at.price <= balance;
  });
}

/** Record a level bought. Keeps the first bought date. */
export function applyPurchase(
  items: OwnedItems,
  id: string,
  level: number,
  day: string,
): OwnedItems {
  const had = items[id];
  return { ...items, [id]: { level, bought: had?.bought ?? day } };
}

/** Merge two sets of owned items: the higher level, the earliest bought date. */
export function mergeItems(a: OwnedItems, b: OwnedItems): OwnedItems {
  const out: OwnedItems = { ...a };
  for (const [id, o] of Object.entries(b)) {
    const had = out[id];
    out[id] = had
      ? {
          level: Math.max(had.level, o.level),
          bought: had.bought < o.bought ? had.bought : o.bought,
        }
      : o;
  }
  return out;
}

// ─── Loadouts and kits ──────────────────────────────────────────────────────

const pad = (slots: readonly (string | null)[] | undefined, n: number): (string | null)[] =>
  Array.from({ length: n }, (_, i) => slots?.[i] ?? null);

/** A ninja's loadout, sized to its slots (extra entries dropped, missing ones empty). */
export function loadoutFor(loadouts: Loadouts, ninja: Ninja): Loadout {
  const l = loadouts[ninja.id];
  return {
    active: pad(l?.active, ninja.slots.active),
    passive: pad(l?.passive, ninja.slots.passive),
  };
}

/**
 * Put an item in a slot (or empty it with null). The item must be of the
 * slot's kind, and it leaves any other slot of that ninja it was in, so a
 * ninja never carries the same item twice. Ownership is checked at fit time.
 */
export function setSlot(
  loadouts: Loadouts,
  ninja: Ninja,
  kind: ItemKind,
  index: number,
  id: string | null,
): Loadouts {
  const current = loadoutFor(loadouts, ninja);
  const slots = [...current[kind]];
  if (index < 0 || index >= slots.length) return loadouts;
  if (id !== null) {
    const item = itemById(id);
    if (!item || item.kind !== kind) return loadouts;
    for (let i = 0; i < slots.length; i++) if (slots[i] === id) slots[i] = null;
  }
  slots[index] = id;
  return { ...loadouts, [ninja.id]: { ...current, [kind]: slots } };
}

function fit(slots: readonly (string | null)[], kind: ItemKind, items: OwnedItems): KitEntry[] {
  const out: KitEntry[] = [];
  for (const id of slots) {
    if (!id || out.some((e) => e.item.id === id)) continue;
    const item = itemById(id);
    const level = levelHeld(items, id);
    const at = item && item.kind === kind ? levelOf(item, level) : undefined;
    if (item && at) out.push({ item, level, at });
  }
  return out;
}

/**
 * The kit a ninja carries into battle: the loadout with the slot count and
 * kind enforced, unowned and repeated items dropped. A battle takes this,
 * never the raw setting.
 */
export function fitFor(ninja: Ninja, loadouts: Loadouts, items: OwnedItems): Kit {
  const l = loadoutFor(loadouts, ninja);
  return { active: fit(l.active, "active", items), passive: fit(l.passive, "passive", items) };
}

/** What the worn items give as the fight starts, and the hooks they set. */
export interface PassiveEffects {
  seconds: number;
  shield: number;
  hints: number;
  /** Share added to the battle's coins. */
  coinShare: number;
  streak: { every: number; seconds: number } | null;
}

export function passiveEffects(kit: Kit): PassiveEffects {
  const out: PassiveEffects = { seconds: 0, shield: 0, hints: 0, coinShare: 0, streak: null };
  for (const { at } of kit.passive) {
    const e: Effect = at.effect;
    switch (e.kind) {
      case "time":
        out.seconds += e.seconds;
        break;
      case "shield":
        out.shield += e.hits;
        break;
      case "hints":
        out.hints += e.count;
        break;
      case "coins":
        out.coinShare += e.share;
        break;
      case "streak":
        out.streak = { every: e.every, seconds: e.seconds + (out.streak?.seconds ?? 0) };
        break;
      default:
        break;
    }
  }
  return out;
}

/** The kit as it goes on an event: ids and levels. */
export function kitRecord(kit: Kit): { id: string; level: number }[] {
  return [...kit.active, ...kit.passive].map((e) => ({ id: e.item.id, level: e.level }));
}

/** "Swift Sandals and Lucky Charm", for Home and the result. */
export function kitNames(kit: Kit): string {
  const names = [...kit.active, ...kit.passive].map((e) => e.item.name);
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
